import { useRef } from 'react';
import { ApolloClient, useApolloClient } from '@apollo/client';
import algoliasearch from 'algoliasearch/lite';

import * as graphql from 'generated/graphql';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import useViewer from './useViewer';

class SearchKey {
	public constructor(
		public readonly appID: string,
		public readonly token: string,
		public readonly expiresAt: Date,
		public readonly client = algoliasearch(appID, token),
		public readonly index = client.initIndex('treams'),
	) {}

	public static async fetch(client: ApolloClient<any>) {
		const response = await client.query({
			query: graphql.SearchKeyDocument,
			fetchPolicy: 'network-only',
		});

		return new SearchKey(
			response.data.searchKey.appID,
			response.data.searchKey.token,
			new Date(response.data.searchKey.expiresAt),
		);
	}

	public isValid() {
		return this.expiresAt > new Date();
	}
}

export default function useImperativeSearch(props?: { excludeViewer?: boolean }) {
	const key = useRef<SearchKey | null>(null);
	const workspace = useWorkspace();
	const client = useApolloClient();
	const viewer = useViewer();
	const now = useRef(Date.now()).current;

	return async (
		query: string,
		options?: {
			filters?: string;
			limit?: number;
			status?: graphql.UserStatus | graphql.UserStatus[];
		},
	) => {
		if (!key.current || !key.current.isValid()) {
			key.current = await SearchKey.fetch(client);
		}

		const filters: string[] = [];

		const statuses = new Set(
			options?.status ? (Array.isArray(options.status) ? options.status : [options.status]) : [],
		);

		if (statuses.size === 3) {
			// Don't filter at all.
		} else if (statuses.size === 2) {
			if (!statuses.has(graphql.UserStatus.Active)) {
				filters.push(
					'(' + [`activatedAt > ${now + 1000 * 60 * 5}`, 'isArchived:true'].join(' OR ') + ')',
				);
			}

			if (!statuses.has(graphql.UserStatus.Inactive)) {
				filters.push(
					'(' + [`activatedAt <= ${now + 1000 * 60 * 5}`, 'isArchived:true'].join(' OR ') + ')',
				);
			}

			if (!statuses.has(graphql.UserStatus.Archived)) {
				filters.push('isArchived:false');
			}
		} else if (statuses.size === 1) {
			if (statuses.has(graphql.UserStatus.Active)) {
				filters.push(
					`isArchived = 0 AND activatedAt <= ${
						now + 1000 * 60 * 5
					} AND (deactivatedAt = 0 OR deactivatedAt > ${now + 1000 * 60 * 5})`,
				);
			} else if (statuses.has(graphql.UserStatus.Inactive)) {
				filters.push(`isArchived = 0 AND activatedAt > ${now + 1000 * 60 * 5}`);
			} else if (statuses.has(graphql.UserStatus.Archived)) {
				filters.push(
					`(isArchived = 1 OR deactivatedAt < ${
						now + 1000 * 60 * 5
					}) AND (isArchived = 1 OR deactivatedAt != 0)`,
				);
			}
		}

		let page = 0;

		const all: any[] = [];

		do {
			const results = await key.current.index.search(query, {
				filters: filters.concat(options?.filters ? [options?.filters] : []).join(' AND '),
				page,
				hitsPerPage: options?.limit ?? 10_000,
			});

			all.push(...results.hits);

			page += 1;

			if (
				page >= results.nbPages ||
				(typeof options?.limit === 'number' && all.length >= options?.limit)
			) {
				break;
			}
		} while (true);

		if (typeof options?.limit === 'number') {
			all.splice(options.limit);
		}

		return all
			.filter((hit) => (props?.excludeViewer ? hit.objectID !== viewer?.id : true))
			.map((hit: any) => ({
				id: hit.objectID,
				firstName: hit['firstName'],
				lastName: hit['lastName'],
				username: hit['username'],
				email: hit['email'],
				avatarID: hit['avatarID'],
				groupID: hit['groupID'],
				avatar: hit['avatarID']
					? {
							source: {
								url: `https://assets.ontreams.com/workspaces/${workspace.id}/assets/${hit['avatarID']}/source`,
							},
					  }
					: undefined,
				lastLoginAt: hit['lastLoginAt'],
			}));
	};
}
